import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { AuthUserCompany, AuthDoctor } from '@/Network/Types/Requests/AuthRequests'
import { AuthResponse } from '@/Network/Types/Responses/AuthResponses'
import { setUserContextInLocalStorage } from '@/router'
import { clearAuthStorage, setAffiliateToken } from '@/utils/storage'
import { ServiceError } from '@/utils/apiError'

export const STORAGE_TOKEN_KEY = ``

/**
 * Fallback quando o backend não manda mensagem (ou a resposta veio sem token).
 * Credencial errada, conta não confirmada etc. chegam com a mensagem do
 * catálogo `AUTH_*` do backend, preservada pelo `handleError`.
 */
export const LOGIN_ERROR_MESSAGE = 'Não foi possível entrar. Tente novamente.'

export default class AuthService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/auth'
  }

  async loginUserCompany(payload: AuthUserCompany): Promise<AuthResponse> {
    try {
      const { data } = await request.post<AuthResponse>(this.getCompletePath('/company'), payload)
      return data
    } catch (e) {
      throw this.handleError(e, LOGIN_ERROR_MESSAGE)
    }
  }

  async loginDoctor(payload: AuthDoctor): Promise<AuthResponse> {
    try {
      const { data } = await request.post<AuthResponse>(this.getCompletePath('/doctor'), payload)
      return data
    } catch (e) {
      throw this.handleError(e, LOGIN_ERROR_MESSAGE)
    }
  }

  persistAuthResponse(payload: AuthResponse) {
    if (payload) {
      clearAuthStorage()
      setUserContextInLocalStorage('company')
      localStorage.setItem('@ME:access_token', `${payload.access_token}`)
      localStorage.setItem('@ME:needChangePassword', `${payload.needChangePassword}`)
      localStorage.setItem('@ME:permissions', JSON.stringify(payload.permissions))
      localStorage.setItem('@ME:name', JSON.stringify(payload.name || 'Minha Escala'))
      localStorage.setItem('@ME:userId', `${payload.userId}`)
    }
  }

  persistDoctorAuthResponse(payload: AuthResponse) {
    if (payload) {
      clearAuthStorage()
      setUserContextInLocalStorage('doctor')
      localStorage.setItem('@ME:doctor_access_token', `${payload.access_token}`)
      localStorage.setItem('@ME:needChangePassword', `${payload.needChangePassword}`)
      localStorage.setItem('@ME:permissions', JSON.stringify(payload.permissions))
      localStorage.setItem('@ME:name', JSON.stringify(payload.name || 'Minha Escala'))
      localStorage.setItem('@ME:userId', `${payload.userId}`)
    }
  }

  /** Persona ANTECIPE_AFFILIATE: só o token de afiliado; nada de empresa/permissões. */
  persistAffiliateAuthResponse(payload: AuthResponse) {
    if (!payload?.access_token) throw new ServiceError(LOGIN_ERROR_MESSAGE, payload)
    clearAuthStorage()
    setUserContextInLocalStorage('affiliate')
    setAffiliateToken(payload.access_token)
  }

  getLocalToken(): string | null {
    try {
      const userContext = localStorage.getItem('@ME:user_context')
      const isDoctorUserContext = userContext == 'doctor'
      if (isDoctorUserContext) {
        const doctorUserToken = localStorage.getItem('@ME:doctor_access_token')
        return doctorUserToken
      }

      const companyUserToken = localStorage.getItem('@ME:access_token')
      return companyUserToken
    } catch (e) {
      // fallback intencional: localStorage inacessível (modo privado/bloqueado) = "não
      // logado". Sem token a request sai sem Authorization, o backend responde 401 e o
      // interceptor leva o usuário à tela de login — o estado visível é o login.
      console.error('Token de autenticação não existe.', e)
      return null
    }
  }

  getLocalDoctorToken(): string | null {
    try {
      const token = localStorage.getItem('@ME:doctor_access_token')
      return token ? token : null
    } catch (e) {
      // fallback intencional: mesmo motivo de `getLocalToken` — sem acesso ao storage,
      // o médico é tratado como não logado e cai na tela de login.
      console.error('Token de autenticação não existe.', e)
      return null
    }
  }

  logout() {
    clearAuthStorage()
    window.location.href = '/'
  }
}
